/**
 * @fileoverview Standard slide-in panel tab strip — the single tab container every
 *             GridPageShell panel uses (datagrid-consistency standard).
 * @module ui-admin/components/PanelTabs
 *
 * Responsibilities: - Render an always-visible horizontal tab bar above panel content.
 *                   - Keep ALL tab content contracted (hidden) until the user clicks a
 *                     tab; clicking the active tab again collapses it.
 *                   - Lazy-mount tab bodies so expensive content (maps, timelines) does
 *                     not render until first expanded.
 * Dependencies:     - React, lucide-react (optional per-tab icons).
 * Architecture:     - Presentation-only, host-neutral. Tab `content` is a ReactNode the
 *                     caller composes (sub-sections inside a tab should use
 *                     AccordionSection, which is collapsed by default). No Dexie, no API,
 *                     no mock data.
 * Notes:            - "Contracted by default" is the standard: `defaultTab` exists only
 *                     for flows that must deep-link into a specific tab.
 *                   - Tab buttons use data-testid={`panel-tab-${id}`} and the panel body
 *                     uses data-testid={`panel-tab-content-${id}`} for the e2e parity spec.
 */

import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * @contract PanelTab
 * Purpose: One entry in the slide-in tab strip.
 * Fields:
 * - id: stable key used for selection state and testids.
 * - label: visible tab text (also the accessible name).
 * - icon: optional lucide icon rendered left of the label.
 * - count: optional numeric badge rendered next to the label.
 * - content: tab body, mounted only while the tab is expanded.
 * - contentClassName: optional className for the tabpanel wrapper — e.g.
 *   'flex flex-col overflow-hidden p-0' for full-bleed content like maps.
 * - disabled: when true the tab renders but cannot be expanded.
 */
export interface PanelTab {
  id: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  content: React.ReactNode;
  contentClassName?: string;
  disabled?: boolean;
}

/**
 * @contract PanelTabsProps
 * Purpose: Configure the standard slide-in tab strip.
 * Fields:
 * - tabs: ordered tab definitions.
 * - defaultTab: optional tab id expanded on mount; omit for fully contracted load
 *   (the standard).
 * - activeTab / onTabChange: controlled-mode pair. When `activeTab` is provided
 *   the component is controlled — pass `null` to render fully contracted.
 * - ariaLabel: accessible label for the tab list (default "Panel sections").
 */
export interface PanelTabsProps {
  tabs: PanelTab[];
  defaultTab?: string;
  activeTab?: string | null;
  onTabChange?: (id: string | null) => void;
  ariaLabel?: string;
}

/**
 * @component PanelTabs
 * Purpose:     Standard contracted-by-default tab strip for GridPageShell slide-in
 *              panels.
 * Props:       PanelTabsProps.
 * Behavior:    Renders the tab bar always; no tab content is visible until a tab is
 *              clicked. Clicking the expanded tab collapses it again. Only the active
 *              tab's content is mounted. Arrow keys move between tabs per the tablist
 *              pattern.
 * Data Source: none (presentation). Callers supply content.
 * Constraints: no API/Dexie access; no mock data.
 */
export function PanelTabs({ tabs, defaultTab, activeTab: activeTabProp, onTabChange, ariaLabel = 'Panel sections' }: PanelTabsProps) {
  const [internalTab, setInternalTab] = useState<string | null>(
    () => (defaultTab && tabs.some((t) => t.id === defaultTab && !t.disabled) ? defaultTab : null),
  );
  // Controlled when the caller supplies activeTab; uncontrolled (contracted by
  // default) otherwise. onTabChange fires in both modes.
  const controlled = activeTabProp !== undefined;
  const activeTab = controlled ? activeTabProp : internalTab;
  const setActiveTab = (id: string | null) => {
    if (!controlled) setInternalTab(id);
    onTabChange?.(id);
  };

  const moveFocus = (e: React.KeyboardEvent<HTMLButtonElement>, dir: -1 | 1) => {
    const bar = e.currentTarget.parentElement;
    if (!bar) return;
    const buttons = Array.from(bar.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
    const idx = buttons.indexOf(e.currentTarget);
    const next = buttons[idx + dir] ?? buttons[dir === 1 ? 0 : buttons.length - 1];
    next?.focus();
  };

  // Roving tabindex: the expanded tab is tabbable; when fully contracted the
  // first enabled tab is tabbable so the strip stays keyboard-reachable.
  const firstEnabledIdx = tabs.findIndex((t) => !t.disabled);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="shrink-0 flex items-stretch gap-1 overflow-x-auto border-b border-(--tp-panel-border,#e5e7eb) px-2"
        style={{ scrollbarWidth: 'thin' }}
        onWheel={(e) => {
          // Map vertical wheel to horizontal scroll so wide tab strips stay usable.
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.currentTarget.scrollLeft += e.deltaY;
        }}
      >
        {tabs.map((tab, index) => {
          const selected = activeTab === tab.id;
          const Icon = tab.icon;
          const tabbable = selected || (activeTab === null && index === firstEnabledIdx);
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`panel-tab-btn-${tab.id}`}
              aria-selected={selected}
              aria-controls={`panel-tab-content-${tab.id}`}
              aria-expanded={selected}
              disabled={tab.disabled}
              data-testid={`panel-tab-${tab.id}`}
              tabIndex={tabbable ? 0 : -1}
              onClick={() => setActiveTab(activeTab === tab.id ? null : tab.id)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') { e.preventDefault(); moveFocus(e, 1); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); moveFocus(e, -1); }
              }}
              className={`-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary,#fb923c) ${
                selected
                  ? 'border-(--brand-primary,#ea580c) text-(--brand-primary,#c2410c)'
                  : 'border-transparent text-(--tp-panel-text-muted,#6b7280) hover:text-(--tp-panel-text,#374151) hover:border-(--tp-panel-border,#d1d5db)'
              } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              {tab.label}
              {tab.count != null && (
                <span
                  className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    selected
                      ? 'bg-(--tp-panel-surface,#fff7ed) text-(--brand-primary,#c2410c)'
                      : 'bg-(--tp-panel-surface,#f3f4f6) text-(--tp-panel-text-muted,#6b7280)'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tabs.map((tab) =>
          activeTab === tab.id ? (
            <div
              key={tab.id}
              role="tabpanel"
              id={`panel-tab-content-${tab.id}`}
              data-testid={`panel-tab-content-${tab.id}`}
              aria-labelledby={`panel-tab-btn-${tab.id}`}
              className={tab.contentClassName ?? 'h-full overflow-y-auto px-3 py-2.5'}
            >
              {tab.content}
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
}

export default PanelTabs;
